import type { MeetlyChunkView, MeetlyChunkState } from '../api/meetly';
import type { LiveChunksState } from '../hooks/useMeetlyLiveChunks';

/**
 * One tile per 20-second chunk of the running meeting, plus the words Meetly
 * heard most recently. This is the "what is going through, what is not" view:
 * a silent chunk is a hollow tile, a quiet one is marked, a failed one is red,
 * and the preview shows the actual transcript coming back a few seconds
 * after each part is spoken.
 */

interface MeetlyChunkStripProps {
  live: LiveChunksState;
  /** Chunks the recorder has cut locally but the server has not listed yet. */
  localCount?: number;
}

const STATE_CLASS: Record<MeetlyChunkState, string> = {
  pending: 'bg-[var(--gt-surface-2)] border-[var(--gt-border)]',
  transcribing: 'bg-[var(--gt-primary-container)] border-[var(--gt-accent-soft)] animate-pulse motion-reduce:animate-none',
  transcribed: 'bg-[var(--gt-primary)] border-[var(--gt-primary)]',
  silent: 'bg-transparent border-[var(--gt-border-strong)] border-dashed',
  failed: 'bg-[#e66a52] border-[#e66a52]',
};

const STATE_LABEL: Record<MeetlyChunkState, string> = {
  pending: 'waiting',
  transcribing: 'transcribing',
  transcribed: 'transcribed',
  silent: 'no speech heard',
  failed: 'could not be transcribed',
};

function clock(seconds: number | null): string {
  if (seconds === null || Number.isNaN(seconds)) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function chunkTitle(chunk: MeetlyChunkView): string {
  const when = chunk.start_time !== null ? ` at ${clock(chunk.start_time)}` : '';
  const words = chunk.state === 'transcribed' ? ` — ${chunk.words} words` : '';
  const level = chunk.level_db !== null ? ` — ${Math.round(chunk.level_db)} dB${chunk.quiet ? ' (quiet)' : ''}` : '';
  return `Part ${chunk.chunk_index + 1}${when}: ${STATE_LABEL[chunk.state]}${words}${level}`;
}

export function heardSummary(counts: LiveChunksState['counts']): string | null {
  const received = counts.received ?? 0;
  if (!received) return null;
  const parts: string[] = [`${counts.transcribed ?? 0} of ${received} parts transcribed`];
  if (counts.silent) parts.push(`${counts.silent} with no speech`);
  if (counts.quiet) parts.push(`${counts.quiet} too quiet`);
  if (counts.failed) parts.push(`${counts.failed} failed`);
  return parts.join(' · ');
}

export default function MeetlyChunkStrip({ live, localCount = 0 }: MeetlyChunkStripProps) {
  const known = live.chunks;
  const placeholders = Math.max(0, localCount - known.length);
  const summary = heardSummary(live.counts);

  return (
    <div className="w-full max-w-md text-left" data-testid="meetly-chunk-strip">
      <div className="flex flex-wrap gap-1" role="list" aria-label="Recording parts">
        {known.map((chunk) => (
          <span
            key={chunk.chunk_index}
            role="listitem"
            title={chunkTitle(chunk)}
            aria-label={chunkTitle(chunk)}
            className={`relative h-3 w-3 rounded-sm border ${STATE_CLASS[chunk.state]}`}
          >
            {chunk.quiet && chunk.state !== 'failed' && (
              <span className="absolute -top-1 -right-1 h-1.5 w-1.5 rounded-full bg-[#e6a23c]" aria-hidden="true" />
            )}
          </span>
        ))}
        {Array.from({ length: placeholders }, (_, i) => (
          <span
            key={`local-${known.length + i}`}
            role="listitem"
            aria-label={`Part ${known.length + i + 1}: uploading`}
            className="h-3 w-3 rounded-sm border border-dashed border-[var(--gt-border)]"
          />
        ))}
      </div>
      {summary && <p className="mt-2 text-xs text-[var(--gt-text-tertiary)]" aria-live="polite">{summary}</p>}
      {live.lastHeard && (
        <div className="mt-3 rounded-xl border border-[var(--gt-border)] bg-[var(--gt-surface-1)] px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--gt-text-tertiary)]">
            Heard{live.lastHeard.start_time !== null ? ` at ${clock(live.lastHeard.start_time)}` : ' so far'}
          </p>
          <p className="mt-1 max-h-24 overflow-y-auto text-sm leading-6 text-[var(--gt-text-secondary)]" data-testid="meetly-heard">
            {live.lastHeard.text}
          </p>
        </div>
      )}
    </div>
  );
}
